import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, InboxIcon, BarChartIcon, FolderIcon, TargetIcon, DatabaseIcon, UsersIcon, ChevronDownIcon, ChevronRightIcon, PinIcon, SettingsIcon, ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react';
interface SidebarItemProps {
  icon: React.ReactNode;
  text: string;
  to: string;
  badge?: number;
  isActive?: boolean;
}
const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  text,
  to,
  badge,
  isActive = false
}) => {
  return <Link to={to} className={`flex items-center h-9 px-3 rounded-md text-sm font-medium ${isActive ? 'bg-indigo-3 text-indigo-11 border-l-3 border-indigo-9' : 'text-gray-12 hover:bg-gray-3'} transition-colors`} data-id="element-356">
      <span className="mr-3" data-id="element-357">{icon}</span>
      <span data-id="element-358">{text}</span>
      {badge && <span className="ml-auto bg-gray-11 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs" data-id="element-359">
          {badge}
        </span>}
    </Link>;
};
interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}
const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  children,
  defaultExpanded = true
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return <div className="mb-2" data-id="element-360">
      <button className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold text-gray-11 uppercase tracking-wider" onClick={() => setExpanded(!expanded)} data-id="element-361">
        <span data-id="element-362">{title}</span>
        {expanded ? <ChevronDownIcon size={16} data-id="element-363" /> : <ChevronRightIcon size={16} data-id="element-364" />}
      </button>
      {expanded && <div className="mt-1 space-y-1 pl-3" data-id="element-365">{children}</div>}
    </div>;
};
export const Layout: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  return <div className="flex h-screen bg-gray-1" data-id="element-366">
      <div className={`fixed top-0 left-0 h-full z-10 bg-white border-r border-gray-6 transition-all ${collapsed ? 'w-16' : 'w-60'}`} data-id="element-367">
        <div className="p-4 flex items-center justify-between border-b border-gray-6" data-id="element-368">
          <div className="flex items-center" data-id="element-369">
            {!collapsed && <>
                <div className="h-8 w-8 bg-indigo-9 text-white rounded flex items-center justify-center mr-2" data-id="element-370">
                  <span className="font-bold" data-id="element-371">D</span>
                </div>
                <span className="font-semibold text-gray-12" data-id="element-372">Dataprint</span>
              </>}
            {collapsed && <div className="h-8 w-8 bg-indigo-9 text-white rounded flex items-center justify-center mx-auto" data-id="element-373">
                <span className="font-bold" data-id="element-374">D</span>
              </div>}
          </div>
          <button className="text-gray-11 hover:text-gray-12" onClick={() => setCollapsed(!collapsed)} data-id="element-375">
            {collapsed ? <ChevronsRightIcon size={20} data-id="element-376" /> : <ChevronsLeftIcon size={20} data-id="element-377" />}
          </button>
        </div>
        <div className="py-4 space-y-1 px-3" data-id="element-378">
          {!collapsed ? <>
              <SidebarItem icon={<HomeIcon size={20} data-id="element-380" />} text="Dashboard" to="/" isActive={location.pathname === '/'} data-id="element-379" />
              <SidebarItem icon={<InboxIcon size={20} data-id="element-382" />} text="Inbox" to="/inbox" badge={22} isActive={location.pathname === '/inbox'} data-id="element-381" />
              <SidebarSection title="Workspace" data-id="element-383">
                <SidebarItem icon={<BarChartIcon size={20} data-id="element-385" />} text="Analysis" to="/analysis" badge={2} isActive={location.pathname === '/analysis'} data-id="element-384" />
                <SidebarItem icon={<TargetIcon size={20} data-id="element-387" />} text="Pursuits" to="/pursuits" isActive={location.pathname === '/pursuits'} data-id="element-386" />
                <SidebarItem icon={<FolderIcon size={20} data-id="element-389" />} text="Asset Library" to="/asset-library" isActive={location.pathname === '/asset-library'} data-id="element-388" />
              </SidebarSection>
              <SidebarSection title="Favorites" data-id="element-390">
                <SidebarItem icon={<PinIcon size={20} data-id="element-392" />} text="Downtown Office" to="/pursuits/downtown-office" isActive={location.pathname === '/pursuits/downtown-office'} data-id="element-391" />
                <SidebarItem icon={<PinIcon size={20} data-id="element-394" />} text="Medical Center" to="/pursuits/medical-center" isActive={location.pathname === '/pursuits/medical-center'} data-id="element-393" />
                <div className="flex items-center h-9 px-3 rounded-md text-sm font-medium text-indigo-9 hover:bg-gray-3 cursor-pointer" data-id="element-395">
                  <span className="mr-3" data-id="element-396">+</span>
                  <span data-id="element-397">Pin Pursuit</span>
                </div>
              </SidebarSection>
              <SidebarSection title="Manage" data-id="element-398">
                <SidebarItem icon={<DatabaseIcon size={20} data-id="element-400" />} text="Database" to="/database" isActive={location.pathname === '/database'} data-id="element-399" />
                <SidebarItem icon={<UsersIcon size={20} data-id="element-402" />} text="Team" to="/team" isActive={location.pathname === '/team'} data-id="element-401" />
              </SidebarSection>
            </> : <>
              <div className="flex flex-col items-center space-y-4 pt-4" data-id="element-403">
                <Link to="/" className={`p-2 rounded-md ${location.pathname === '/' ? 'bg-indigo-3 text-indigo-11' : 'text-gray-11 hover:bg-gray-3'}`} data-id="element-404">
                  <HomeIcon size={20} data-id="element-405" />
                </Link>
                <Link to="/inbox" className={`p-2 rounded-md relative ${location.pathname === '/inbox' ? 'bg-indigo-3 text-indigo-11' : 'text-gray-11 hover:bg-gray-3'}`} data-id="element-406">
                  <InboxIcon size={20} data-id="element-407" />
                  <span className="absolute top-0 right-0 bg-gray-11 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]" data-id="element-408">
                    22
                  </span>
                </Link>
                <Link to="/analysis" className={`p-2 rounded-md ${location.pathname === '/analysis' ? 'bg-indigo-3 text-indigo-11' : 'text-gray-11 hover:bg-gray-3'}`} data-id="element-409">
                  <BarChartIcon size={20} data-id="element-410" />
                </Link>
                <Link to="/pursuits" className={`p-2 rounded-md ${location.pathname === '/pursuits' ? 'bg-indigo-3 text-indigo-11' : 'text-gray-11 hover:bg-gray-3'}`} data-id="element-411">
                  <TargetIcon size={20} data-id="element-412" />
                </Link>
                <Link to="/asset-library" className={`p-2 rounded-md ${location.pathname === '/asset-library' ? 'bg-indigo-3 text-indigo-11' : 'text-gray-11 hover:bg-gray-3'}`} data-id="element-413">
                  <FolderIcon size={20} data-id="element-414" />
                </Link>
                <Link to="/database" className={`p-2 rounded-md ${location.pathname === '/database' ? 'bg-indigo-3 text-indigo-11' : 'text-gray-11 hover:bg-gray-3'}`} data-id="element-415">
                  <DatabaseIcon size={20} data-id="element-416" />
                </Link>
                <Link to="/team" className={`p-2 rounded-md ${location.pathname === '/team' ? 'bg-indigo-3 text-indigo-11' : 'text-gray-11 hover:bg-gray-3'}`} data-id="element-417">
                  <UsersIcon size={20} data-id="element-418" />
                </Link>
              </div>
            </>}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-6" data-id="element-419">
          {!collapsed ? <div className="flex items-center justify-between" data-id="element-420">
              <div className="flex items-center" data-id="element-421">
                <div className="h-8 w-8 bg-gray-4 rounded-full flex items-center justify-center mr-2" data-id="element-422">
                  <span className="font-medium text-gray-11" data-id="element-423">JD</span>
                </div>
                <div className="text-sm" data-id="element-424">
                  <div className="font-medium text-gray-12" data-id="element-425">John Doe</div>
                </div>
              </div>
              <button className="text-gray-11 hover:text-gray-12" data-id="element-426">
                <SettingsIcon size={20} data-id="element-427" />
              </button>
            </div> : <div className="flex justify-center" data-id="element-428">
              <div className="h-8 w-8 bg-gray-4 rounded-full flex items-center justify-center" data-id="element-429">
                <span className="font-medium text-gray-11" data-id="element-430">JD</span>
              </div>
            </div>}
        </div>
      </div>
      <div className={`flex-1 transition-all ${collapsed ? 'ml-16' : 'ml-60'} bg-gray-1 min-h-screen`} data-id="element-431">
        <main className="p-8 max-w-[1440px] mx-auto" data-id="element-432">{children}</main>
      </div>
    </div>;
};